<template>
  <ModalComp
    :isVisible="visible"
    title="Mise en avant des produits"
    size="md"
    hideFooter
    @update:isVisible="$emit('update:visible', $event)"
  >
    <template #body>
      <div class="p-4">
        <!-- Jetons restants -->
        <div class="mb-6">
          <div class="flex items-center justify-between bg-blue-50 border border-blue-200 text-blue-900 rounded-lg px-4 py-3">
            <div class="flex items-center gap-2">
              <i class="fas fa-coins"></i>
              <span class="text-sm">Jetons restants ce mois-ci</span>
            </div>
            <div class="font-semibold">
              {{ remainingJetons }} / {{ monthlyLimit }}
            </div>
          </div>
        </div>

        <!-- Formulaire -->
        <div class="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Gestion des MEA</h3>
          
          <!-- Checkboxes -->
          <div v-if="false" class="flex gap-6 mb-4">
            <label class="flex items-center gap-3 cursor-pointer">
              <input 
                type="checkbox" 
                v-model="formData.reassort"
                class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              >
              <span class="text-sm font-medium text-gray-700">Reassort</span>
            </label>
            
            <label class="flex items-center gap-3 cursor-pointer">
              <input 
                type="checkbox" 
                v-model="formData.nouveaute"
                class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              >
              <span class="text-sm font-medium text-gray-700">Nouveautés</span>
            </label>
          </div>

          <!-- Champ de référence avec autocomplétion -->
          <div class="relative mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Ajouter une Référence base :
            </label>
            <div class="relative">
              <input 
                type="text"
                v-model.trim="newReference"
                @input="handleInput"
                @keydown.enter.prevent="addReference"
                @keydown.down.prevent="selectNext"
                @keydown.up.prevent="selectPrevious"
                @keydown.escape="closeAutocomplete"
                @blur="handleBlur"
                @focus="handleFocus"
                placeholder="Référence base"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <!-- Liste d'autocomplétion -->
              <div 
                v-if="showAutocomplete"
                class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
              >
                <div v-if="isSearching" class="px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
                  <i class="fas fa-spinner fa-spin"></i>
                  Recherche...
                </div>
                <div v-else-if="lastAutocompleteError" class="px-3 py-2 text-sm text-red-600">
                  {{ lastAutocompleteError }}
                </div>
                <template v-else>
                  <div
                    v-for="(result, index) in autocompleteResults"
                    :key="index"
                    @click="selectReference(result.reference_base)"
                    :class="[
                      'px-3 py-2 cursor-pointer hover:bg-blue-50',
                      selectedIndex === index ? 'bg-blue-100' : ''
                    ]"
                  >
                    {{ result.reference_base }}
                  </div>
                  <div v-if="autocompleteResults.length === 0" class="px-3 py-2 text-sm text-gray-500">
                    Aucun résultat
                  </div>
                </template>
              </div>
            </div>
          </div>

          <hr class="my-4 border-gray-200" />

          <!-- Liste des références -->
          <div class="max-h-40 overflow-y-auto mb-4 border rounded-md">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Référence</th>
                  <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="ref in references" :key="ref" class="hover:bg-gray-50">
                  <td class="px-4 py-2 text-sm text-gray-900">{{ ref }}</td>
                  <td class="px-4 py-2">
                    <button @click="removeReference(ref)" class="text-red-600 hover:text-red-900">
                      <i class="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="references.length === 0">
                  <td colspan="2" class="px-4 py-4 text-center text-sm text-gray-500">Aucune référence</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Bouton de soumission -->
          <div class="flex justify-center mt-6">
            <button 
              @click="submitForm"
              :disabled="!canSubmit || isLoading || references.length > remainingJetons || remainingJetons === 0"
              class="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 transition-colors"
            >
              <i v-if="isLoading" class="fas fa-spinner fa-spin mr-2"></i>
              <i v-else class="fas fa-check mr-2"></i>
              Mettre en avant ({{ references.length }})
            </button>
          </div>
        </div>
      </div>
    </template>
  </ModalComp>

  <!-- Confirmation Modal -->
  <ModalComp
    :isVisible="showConfirmModal"
    title="Confirmer la mise en avant"
    size="sm"
    hideFooter
    @update:isVisible="showConfirmModal = $event"
  >
    <template #body>
      <div class="p-4">
        <p class="text-sm text-gray-600 mb-4">{{ confirmModalMessage }}</p>
        <div class="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto mb-4">
          <ul class="space-y-1">
            <li v-for="ref in references" :key="ref" class="text-xs text-gray-700">
              <i class="fas fa-check text-green-500 mr-2"></i>{{ ref }}
            </li>
          </ul>
        </div>
        <div class="flex justify-end gap-3 mt-4">
          <button @click="showConfirmModal = false" class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
            Annuler
          </button>
          <button @click="confirmSubmit" class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700">
            Confirmer
          </button>
        </div>
      </div>
    </template>
  </ModalComp>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted, onUnmounted } from 'vue'
import { ModalComp } from '@/components'
import { useNotification } from '@/composables/useNotification'
import { 
  getMeaAutocomplete, 
  updateMeaProducts, 
  checkMonthlyMeaBeforeAdd,
} from '@/api/mea'

const props = defineProps<{
  visible: boolean
  initialReferences?: string[]
}>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'success'): void
}>()

const { success: showSuccessToast, error: showErrorToast } = useNotification()

// État local
const newReference = ref('')
const references = ref<string[]>([])
const isLoading = ref(false)
const monthlyLimit = ref(100)
const remainingJetons = ref(100)
const autocompleteResults = ref<Array<{ reference_base: string }>>([])
const showAutocomplete = ref(false)
const selectedIndex = ref(-1)
const autocompleteTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const showConfirmModal = ref(false)
const confirmModalMessage = ref('')
const isSearching = ref(false)
const lastAutocompleteError = ref<string | null>(null)

const formData = reactive({
  reassort: true,
  nouveaute: true
})

// Sync initial references when modal opens
watch(() => props.visible, (newVal) => {
  if (newVal) {
    if (props.initialReferences && props.initialReferences.length > 0) {
      // Filtrer les doublons et les valeurs vides
      const uniqueRefs = Array.from(new Set(props.initialReferences.filter(r => !!r)))
      references.value = [...uniqueRefs]
    } else {
      references.value = []
    }
    refreshRemainingJetons()
  }
}, { immediate: true })

const canSubmit = computed(() => {
  return (formData.reassort || formData.nouveaute) && references.value.length > 0
})

// Autocomplétion
const handleInput = async () => {
  if (autocompleteTimeout.value) clearTimeout(autocompleteTimeout.value)
  const term = newReference.value.trim()
  if (term.length < 2) {
    autocompleteResults.value = []
    showAutocomplete.value = false
    return
  }

  autocompleteTimeout.value = setTimeout(async () => {
    try {
      isSearching.value = true
      showAutocomplete.value = true
      const results = await getMeaAutocomplete(term, references.value.join('~'))
      autocompleteResults.value = results
      selectedIndex.value = -1
    } catch (error) {
      lastAutocompleteError.value = 'Erreur lors de la recherche'
    } finally {
      isSearching.value = false
    }
  }, 300)
}

const handleFocus = () => {
  if (newReference.value.trim().length >= 2) showAutocomplete.value = true
}

const handleBlur = () => {
  setTimeout(() => { showAutocomplete.value = false }, 200)
}

const closeAutocomplete = () => {
  showAutocomplete.value = false
  selectedIndex.value = -1
}

const selectNext = () => {
  if (selectedIndex.value < autocompleteResults.value.length - 1) selectedIndex.value++
}

const selectPrevious = () => {
  if (selectedIndex.value > 0) selectedIndex.value--
}

const selectReference = (reference: string) => {
  newReference.value = reference
  showAutocomplete.value = false
  selectedIndex.value = -1
  addReference()
}

const addReference = async () => {
  let ref = newReference.value.trim()
  if (selectedIndex.value >= 0 && autocompleteResults.value[selectedIndex.value]) {
    ref = autocompleteResults.value[selectedIndex.value].reference_base
  }
  if (!ref) return
  if (references.value.includes(ref)) {
    showErrorToast('Déjà dans la liste')
    return
  }

  // Validation rapide
  try {
    const results = await getMeaAutocomplete(ref, references.value.join('~'))
    if (results.some(r => r.reference_base === ref)) {
      references.value.push(ref)
      newReference.value = ''
      autocompleteResults.value = []
      showAutocomplete.value = false
    } else {
      showErrorToast('Référence introuvable')
    }
  } catch (e) {
    showErrorToast('Erreur de validation')
  }
}

const removeReference = (ref: string) => {
  references.value = references.value.filter(r => r !== ref)
}

const submitForm = async () => {
  if (!canSubmit.value) return
  isLoading.value = true
  try {
    const limitCheck = await checkMonthlyMeaBeforeAdd(references.value.length)
    if (!limitCheck.allowed) {
      showErrorToast(`Limite dépassée. Jetons restants: ${limitCheck.limit - limitCheck.currentCount}`)
      isLoading.value = false
      return
    }
    const typeText = formData.reassort && formData.nouveaute ? 'réassort et nouveautés' : (formData.reassort ? 'réassort' : 'nouveautés')
    confirmModalMessage.value = `Voulez-vous mettre en avant ${references.value.length} produit(s) en ${typeText} ?`
    showConfirmModal.value = true
  } catch (error) {
    showErrorToast('Erreur de vérification')
  } finally {
    isLoading.value = false
  }
}

const confirmSubmit = async () => {
  showConfirmModal.value = false
  isLoading.value = true
  try {
    const updated = await updateMeaProducts(references.value, {
      reassort: formData.reassort,
      nouveautes: formData.nouveaute,
    })
    if (updated > 0) {
      showSuccessToast(`${updated} produits mis en avant !`)
      await refreshRemainingJetons()
      emit('success')
      emit('update:visible', false)
    } else {
      showErrorToast('Ces produits ont déjà été mis en avant aujourd\'hui')
    }
  } catch (error) {
    showErrorToast('Erreur lors de la mise à jour')
  } finally {
    isLoading.value = false
  }
}

const refreshRemainingJetons = async () => {
  try {
    const check = await checkMonthlyMeaBeforeAdd(0)
    monthlyLimit.value = check.limit
    remainingJetons.value = Math.max(0, check.limit - check.currentCount)
  } catch (e) {
    remainingJetons.value = monthlyLimit.value
  }
}

onMounted(refreshRemainingJetons)
onUnmounted(() => { if (autocompleteTimeout.value) clearTimeout(autocompleteTimeout.value) })
</script>
